import React, { useCallback, useEffect, useMemo, useRef } from 'react'

// Inbound email bodies are third-party HTML: we do not control them and cannot trust
// them. Injecting one inline (dangerouslySetInnerHTML) hands the sender the app
// document -- event-handler attributes execute, the email's CSS bleeds into our
// layout, and a single <base href> rewrites document.baseURI for the whole page,
// silently repointing every relative URL. That last one is what broke GraphQL: the
// Apollo endpoint is the relative '/graphiql/', so an email carrying
// <base href="https://e.mail.ru/"> sent every subsequent query to
// https://e.mail.ru/graphiql/ and it died on CORS.
//
// A sandboxed iframe gives the email its own document, so none of that can reach us.

// Deliberately WITHOUT allow-scripts: combining it with allow-same-origin would let
// the framed content reach up and strip its own sandbox, which defeats the isolation
// entirely. allow-same-origin is here only so we can read the frame's height from the
// parent; with scripting off, nothing inside can act on that access.
const FRAME_SANDBOX = 'allow-same-origin allow-popups'

// Matches the surrounding thread so plain-text-ish emails do not look foreign. The
// frame gets no inherited styles, so anything we want has to be stated here.
const FRAME_STYLES = `
    html, body {
        margin: 0;
        padding: 0;
        background: transparent;
    }
    body {
        font-family: 'Rubik', sans-serif;
        font-size: 13px;
        color: #273846cc;
        overflow-wrap: break-word;
        word-wrap: break-word;
    }
    img, video {
        max-width: 100%;
        height: auto;
    }
    table {
        max-width: 100%;
    }
    blockquote {
        margin: 8px 0;
        padding-left: 10px;
        border-left: 2px solid rgba(39, 56, 70, 0.15);
        color: #27384699;
    }
`

export function buildEmailFrameSrcDoc(html) {
    return [
        '<!doctype html><html><head>',
        '<meta charset="utf-8">',
        // Keep internal app URLs out of the Referer when a recipient clicks through.
        '<meta name="referrer" content="no-referrer">',
        // Default every link to a new tab so a click cannot replace the thread with a
        // remote page. This has to precede the email's own markup: the first <base> in
        // document order wins the `target` slot. Any <base href> the email ships still
        // applies, but only inside this frame, which is exactly where we want it.
        '<base target="_blank">',
        `<style>${FRAME_STYLES}</style>`,
        '</head><body>',
        html || '',
        '</body></html>',
    ].join('')
}

// contentDocument access can throw if the frame is not same-origin after all; treat
// any failure as "cannot measure" rather than letting it break the thread render.
const frameDocument = (frame) => {
    try {
        return frame?.contentDocument ?? null
    } catch {
        return null
    }
}

export default function EmailMessageBody({ html, title = 'Email message content' }) {
    const frameRef = useRef(null)
    const srcDoc = useMemo(() => buildEmailFrameSrcDoc(html), [html])

    // Height is driven imperatively rather than through state: the frame reports a new
    // height on every image that settles, and routing each one through a re-render of
    // the whole thread is needless churn.
    const applyHeight = useCallback(() => {
        const frame = frameRef.current
        const doc = frameDocument(frame)
        if (!doc?.body) return

        const measured = Math.max(doc.body.scrollHeight || 0, doc.documentElement?.scrollHeight || 0)
        const nextHeight = `${Math.ceil(measured)}px`
        if (measured > 0 && frame.style.height !== nextHeight) frame.style.height = nextHeight
    }, [])

    useEffect(() => {
        const frame = frameRef.current
        if (!frame) return undefined

        const trackedImages = []

        const handleLoad = () => {
            applyHeight()

            const doc = frameDocument(frame)
            if (!doc?.body) return

            doc.querySelectorAll('img').forEach((image) => {
                if (image.complete) return
                image.addEventListener('load', applyHeight)
                image.addEventListener('error', applyHeight)
                trackedImages.push(image)
            })
        }

        frame.addEventListener('load', handleLoad)
        // srcdoc can already be parsed by the time this effect runs, in which case the
        // load event has been and gone.
        if (frameDocument(frame)?.readyState === 'complete') handleLoad()

        return () => {
            frame.removeEventListener('load', handleLoad)
            trackedImages.forEach((image) => {
                image.removeEventListener('load', applyHeight)
                image.removeEventListener('error', applyHeight)
            })
        }
    }, [srcDoc, applyHeight])

    if (!html) return null

    return (
        <iframe
            ref={frameRef}
            className="CommsMessageBodyFrame"
            title={title}
            sandbox={FRAME_SANDBOX}
            srcDoc={srcDoc}
            loading="lazy"
        />
    )
}
